import React, { Fragment, useEffect, useState } from "react";
import ActionBar from "../../common/ActionBar";
import labels from "../../../helpers/labels.json";
import _ from "lodash";
import { Form, Formik } from "formik";
import { navigate } from "@reach/router";
import { CreateTranscriber } from "../../../helpers/validationSchema";
import FormField from "../../common/FormField/FormField";
import { PrimaryCTAButton } from "../../common/Buttons";
import styled from "styled-components";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import useLoader from "../../../hooks/useLoader";
import TranscriberService from "../../../services/TranscriberService";
import { getErrorMessage } from "../../../helpers/functions";
import { useSnackbar } from "notistack";
import { FormLabel } from "@material-ui/core";
import { CustomCheckbox } from "../../common/FormInputs";

const CreateCaseManager = ({ id }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState();
  const [changePassword, setChangePassword] = useState(false);

  const breadcrumbs = [
    _.startCase(labels.transcriber),
    _.startCase(id ? labels.edit_transcriber : labels.create_transcriber),
  ];
  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case "transcribers":
        navigate("/dashboard/case-manager/transcriber");
        break;
      default:
        break;
    }
  };

  const baseInputs = [
    {
      name: "name",
      type: "text",
      label: "Name of the Transcriber",
      required: true,
    },
    {
      name: "email",
      type: "email",
      label: "Email of the Transcriber",
      required: true,
    },
    {
      name: "mobile",
      type: "text",
      label: "Phone no of the Transcriber",
      required: true,
    },
  ];

  const passwordInput = {
    name: "password",
    type: "password",
    label: "Password",
    required: !id,
  };

  const inputs = !id
    ? [...baseInputs, passwordInput]
    : changePassword
      ? [...baseInputs, passwordInput]
      : baseInputs;

  const checkbox_inputs = [
    {
      name: "isDashboard",
      type: "checkbox",
      label: "Dashboard Access",
      required: false,
    },
    {
      name: "isAllJobsAccess",
      type: "checkbox",
      label: "All Jobs Access",
      required: false,
    },
    {
      name: "isAssignAccess",
      type: "checkbox",
      label: "Assign Access",
      required: false,
    },
  ];

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: "Loading..." });
      const { password, changePassword: _cp, ...rest } = values;
      const payload = { ...rest };

      if (!id) {
        payload.password = password;
      } else if (changePassword) {
        payload.password = password;
      }

      if (id) {
        const response = await TranscriberService.editTranscriber(payload, id);
        if (response) {
          enqueueSnackbar(response.message, {
            variant: "success",
          });
          navigate("/dashboard/case-manager/transcriber");
        }
      } else {
        const response = await TranscriberService.createTranscriber(payload);
        if (response) {
          enqueueSnackbar(response.message, {
            variant: "success",
          });
          navigate("/dashboard/case-manager/transcriber");
        }
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  useEffect(() => {
    async function getTranscriberDetails(id) {
      try {
        setLoader({ state: true, message: "Fetching transcriber details..." });
        const response = await TranscriberService.getTranscriberDetails(id);
        if (response) {
          setState(response?.transcriberDetails);
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }

    if (id) {
      getTranscriberDetails(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <Fragment>
      <ActionBar {...{ breadcrumbs, onBreadcrumbClick }} />
      <PageWrap>
        <Formik
          initialValues={{
            name: state?.name || "",
            email: state?.email || "",
            mobile: state?.mobile || "",
            password: "",
            changePassword: changePassword,
            isDashboard: state?.isDashboard || false,
            isAllJobsAccess: state?.isAllJobsAccess || false,
            isAssignAccess: state?.isAssignAccess || false,
            type: id ? "EDIT" : "CREATE",
          }}
          validationSchema={CreateTranscriber}
          enableReinitialize
          onSubmit={onFormSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            handleChange,
            errors,
            handleSubmit,
            touched,
            handleBlur,
            isValid,
            setFieldValue,
            setFieldError,
          }) => (
            <StyledForm>
              <Form>
                <Card>
                  <CardHeader>
                    <BackArrow
                      onClick={() =>
                        navigate(`/dashboard/case-manager/transcriber`)
                      }
                      src={require("../../../assets/images/backArrow.svg")}
                    />
                    <div>
                      <Heading>
                        {_.startCase(
                          id
                            ? labels.edit_transcriber
                            : labels.create_transcriber,
                        )}
                      </Heading>
                      <SubHeading>
                        {id
                          ? "Update this transcriber's details and access"
                          : "Set up login details and access for a new transcriber"}
                      </SubHeading>
                    </div>
                  </CardHeader>

                  <SectionLabel>Details</SectionLabel>
                  <FieldGrid>
                    {inputs?.map((input, key) => (
                      <FormField
                        white
                        {...{
                          key,
                          input,
                          values,
                          errors,
                          touched,
                          handleBlur,
                          handleChange,
                          setFieldValue,
                        }}
                      />
                    ))}
                  </FieldGrid>

                  {id && (
                    <PasswordToggleRow>
                      {!changePassword ? (
                        <ChangePasswordLink
                          type="button"
                          onClick={() => {
                            setChangePassword(true);
                            setFieldValue("changePassword", true);
                          }}
                        >
                          Change password
                        </ChangePasswordLink>
                      ) : (
                        <ChangePasswordLink
                          type="button"
                          $muted
                          onClick={() => {
                            setChangePassword(false);
                            setFieldValue("changePassword", false);
                            setFieldValue("password", "");
                            setFieldError("password", undefined);
                          }}
                        >
                          Cancel password change
                        </ChangePasswordLink>
                      )}
                    </PasswordToggleRow>
                  )}

                  <Divider />

                  <SectionLabel>Transcriber Preference</SectionLabel>
                  <FormLabel
                    style={{
                      fontFamily: theme.fonts.primaryFontSemiBold,
                      fontSize: 12,
                      color: COLORS.INPUT_LABEL,
                      display: "none",
                    }}
                  >
                    {"Transcriber Preference"}
                  </FormLabel>
                  <CheckboxGrid>
                    {checkbox_inputs?.map((pre, index) => (
                      <CheckboxCard
                        key={index}
                        $checked={!!values[pre?.name]}
                        htmlFor={pre?.name}
                      >
                        <CustomCheckbox
                          checkboxmargin
                          disabled={pre?.disabled}
                          type={pre?.type}
                          value={values[pre?.name]}
                          checked={values[pre?.name]}
                          error={errors[pre?.name] && touched[pre?.name]}
                          helperText={touched[pre?.name] && errors[pre?.name]}
                          id={pre?.name}
                          name={pre?.name}
                          onChange={(e) =>
                            setFieldValue(pre?.name, e.target.checked)
                          }
                          onBlur={handleBlur}
                          style={{ marginTop: 0 }}
                        />
                        <CheckboxLabel>{pre?.label}</CheckboxLabel>
                      </CheckboxCard>
                    ))}
                  </CheckboxGrid>

                  <ActionRow>
                    <PrimaryCTAButton onClick={handleSubmit}>
                      {id ? "Update Transcriber" : "Create Transcriber"}
                    </PrimaryCTAButton>
                  </ActionRow>
                </Card>
              </Form>
            </StyledForm>
          )}
        </Formik>
      </PageWrap>
    </Fragment>
  );
};

export default CreateCaseManager;

export const PageWrap = styled.div`
  display: flex;
  padding: 32px 24px 64px;
`;

export const StyledForm = styled.form`
  width: 100%;
  max-width: 640px;
`;

export const Card = styled.div`
  background: #ffffff;
  border: 1px solid rgba(20, 20, 20, 0.06);
  border-radius: 14px;
  box-shadow:
    0 1px 2px rgba(16, 24, 40, 0.04),
    0 8px 24px rgba(16, 24, 40, 0.04);
  padding: 32px 36px 36px;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 28px;
`;

export const BackArrow = styled.img`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
  margin-top: 4px;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.6;
  }
`;

export const Heading = styled.div`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 20px;
  line-height: 1.3;
  color: ${COLORS.COLOR_DARK};
`;

export const SubHeading = styled.div`
  font-family: ${theme.fonts.primaryFont};
  font-size: 13px;
  color: ${COLORS.INPUT_LABEL || "#8A8A8A"};
  margin-top: 4px;
`;

export const SectionLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  letter-spacing: 0.2px;
  color: ${COLORS.INPUT_LABEL || "#8A8A8A"};
  margin-bottom: 12px;
`;

export  const FieldGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const PasswordToggleRow = styled.div`
  margin-top: 4px;
  margin-bottom: 8px;
`;

export const ChangePasswordLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${(props) =>
    props.$muted ? COLORS.INPUT_LABEL || "#8A8A8A" : COLORS.BTN_GREEN};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.75;
    text-decoration: underline;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(20, 20, 20, 0.06);
  margin: 24px 0 20px;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
`;

const CheckboxCard = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid
    ${(props) => (props.$checked ? COLORS.BTN_GREEN : "rgba(20, 20, 20, 0.08)")};
  background: ${(props) =>
    props.$checked ? "rgba(91, 95, 239, 0.06)" : "#FAFAFA"};
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
`;

const CheckboxLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
`;

export const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
`;
